/**
 * Pipeline health — per-worker-role DB query latency (p50/p99) and per-queue
 * consumer-lag status.
 *
 * THE GAP THIS CLOSES: a multi-day pipeline throughput collapse went
 * completely invisible until someone happened to check the Elasticsearch
 * dashboard. Every panel beside this one on the Collector tab (Queues,
 * Throughput, Budget) reads Redis directly, and Redis looked fine throughout
 * — the degradation was in the database underneath it. Nothing surfaced "the
 * database is slow" as its OWN signal, distinct from "the queue is deep" or
 * "nothing is draining it" (Throughput.tsx's own module docstring). This
 * panel is that missing signal: DB latency per worker role, and queue lag
 * with a server-computed severity, read from the one route on this board
 * that promises to answer with the truth right now rather than a cached one.
 *
 * LIVE, NOT A SNAPSHOT — and said so explicitly, in contrast to the vidIQ
 * tier panel and every load-once report on this board. `/metrics/pipeline-
 * health` is DELIBERATELY UNCACHED (no `cached`/`generated_at` wrapper,
 * unlike almost everything else this board reads), so `usePipelineHealth`
 * polls it on a timer (see that hook's own comment in api/queries.ts) rather
 * than waiting for a click — there is no "press refresh to get a newer
 * reading" here, because every reading already is the newest one.
 *
 * NULL IS NOT ZERO, restated for a fourth shape on this board (Queues.
 * rate_per_s, Throughput.per_s, EmbeddingsLiveService.docs_per_s): a queue
 * whose group/stream was not found reports `status: "unknown"` with
 * `lag`/`maxlen`/`pct` ALL null, and that must render as a dash, never as a
 * fabricated "0%" bar — a truly idle queue (`lag: 0`) is a different,
 * ordinary fact and must render as a real, visible zero.
 */
import { usePipelineHealth } from '../api/queries'
import { PanelState, Pill, Tile } from '../components/ui'
import type { State } from '../components/ui'
import { ms, n, pct } from '../components/format'
import { PIPELINE_HEALTH_QUEUE_KEYS } from '../api/schemas'
import type { PipelineHealthQueueKey } from '../api/schemas'

/** Human labels for the six FIXED queue keys the endpoint always reports.
 *  Kept as a lookup, not a per-key branch — the row set itself always comes
 *  from `PIPELINE_HEALTH_QUEUE_KEYS`, never from whatever the payload happens
 *  to contain, so a queue missing from a hostile/partial response still gets
 *  its own row instead of silently vanishing from the table. */
const QUEUE_LABELS: Record<PipelineHealthQueueKey, string> = {
  'channels.refresh': 'channels · refresh',
  'channels.discover': 'channels · discover',
  'videos.refresh': 'videos · refresh',
  'keywords.search': 'keywords · search',
  'trending.regions': 'trending · regions',
  seeds: 'seeds',
}

/** The server computes the bucket (`pct < 0.4` ok, `0.4–0.7` warning,
 *  `>= 0.7` critical, or `unknown` when the group/stream was not found at
 *  all) — this maps that STRING to the board's existing severity vocabulary
 *  (Pill's `ok`/`warn`/`crit`/`idle`, the same one Divergence.tsx and
 *  StatusBand already use) rather than recomputing the thresholds
 *  client-side. An unrecognised status (a future value this dashboard has
 *  not been told about yet) falls back to the neutral `idle` state rather
 *  than a guessed color. */
const STATUS_STATE: Record<string, State> = {
  ok: 'ok', warning: 'warn', critical: 'crit', unknown: 'idle',
}

/** "lag / maxlen (pct%)" for one queue row. `pct` is a fraction (0.42, not
 *  42), so it is scaled here, once, right before display — everywhere else
 *  in this file it stays the raw 0..1 the server sends. A null `pct` (the
 *  `unknown` state, always alongside null `lag`/`maxlen`) renders as "—",
 *  never `pct(0)`, which would read as a real, calm 0% instead of "not
 *  found". `n()`/`pct()` already treat every other null as a dash and every
 *  real zero (`lag: 0`, `pct: 0`) as a real, visible zero — the exact
 *  distinction this row exists to get right. */
function lagCell(lag: number | null, maxlen: number | null, pctFraction: number | null): string {
  return `${n(lag)} / ${n(maxlen)} (${pctFraction === null ? '—' : pct(pctFraction * 100)})`
}

export function PipelineHealth() {
  const { data, isLoading, error } = usePipelineHealth()

  // Defensive read, per the module docstring: the schema validates the shape
  // at the fetch boundary, but a role/key that is merely absent or malformed
  // in a backend built in parallel must degrade this panel, not crash it.
  const roles = Object.entries(data?.db_latency_ms ?? {}).sort(([a], [b]) => a.localeCompare(b))

  return (
    <Tile
      title="Pipeline Health"
      hint="live — polled every 10s, never cached"
    >
      <PanelState isLoading={isLoading} error={error}>
        {!data ? null : (
          <>
            <div className="sec">
              <h2>Batch duration</h2>
              <span className="rule" />
              <span className="hint mono">p50 / p99 · whole batch, including quota back-off waits</span>
            </div>
            {roles.length === 0 ? (
              <p className="panel-load">
                no worker role has run a database query recently — not an error,
                just nothing to report yet
              </p>
            ) : (
              <table className="t">
                <thead>
                  <tr>
                    <th>role</th>
                    <th className="n">p50</th>
                    <th className="n">p99</th>
                    <th className="n">samples</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map(([role, m]) => (
                    <tr key={role}>
                      <td>{role}</td>
                      <td className="n">{ms(m?.p50 ?? null)}</td>
                      <td className="n">{ms(m?.p99 ?? null)}</td>
                      <td className="n q">{n(m?.samples ?? null)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="sec">
              <h2>Queue Lag</h2>
              <span className="rule" />
              <span className="hint mono">lag / capacity (% full)</span>
            </div>
            <table className="t">
              <thead>
                <tr>
                  <th>queue</th>
                  <th className="n">lag / maxlen</th>
                  <th className="n">status</th>
                </tr>
              </thead>
              <tbody>
                {PIPELINE_HEALTH_QUEUE_KEYS.map((key) => {
                  // Every one of the six keys renders ITS OWN row always, even
                  // when the payload omits it outright (hostile/partial data)
                  // — an "unknown" row is informative; a silently missing row
                  // is not.
                  const row = data.queue_lag?.[key]
                  const status = row?.status ?? 'unknown'
                  const lag = row?.lag ?? null
                  const maxlen = row?.maxlen ?? null
                  const pctFraction = row?.pct ?? null
                  return (
                    <tr key={key}>
                      <td>{QUEUE_LABELS[key]}</td>
                      <td className="n q">{lagCell(lag, maxlen, pctFraction)}</td>
                      <td className="n">
                        <Pill state={STATUS_STATE[status] ?? 'idle'}>{status}</Pill>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <p className="note">
              Live, not a window average and not a manual snapshot: this endpoint is
              deliberately uncached, so every reading above is the database's and the
              queues' actual state right now, refreshed automatically every 10s. Unlike
              the vidIQ tier snapshot elsewhere on this board, there is no refresh
              button here — there is nothing to press, because the number on screen is
              never more than a few seconds old.
            </p>
          </>
        )}
      </PanelState>
    </Tile>
  )
}
